import { OptionButton } from "@/react/components/OptionButton";

import { routes, useSimpleKitContext } from "@/react/context/SimpleKitContext";
import { useAccount, useBalance, useChainInfo } from "@/react/hooks";
import { Skeleton } from "@/react/shadcn/ui/skeleton";
import { cn } from "@/react/shadcn/utils";
import { motion } from "framer-motion";
import { AccountLogo, WithdrawLogo, WrongChainLogo } from "based-assets/index";
import { CopyAddressButton } from "@/react/components/CopyAddressButton";
import { minifyAddress } from "based-shared/utils/minify-address";
import { QrCodeIcon } from "lucide-react";
import { formatUnits } from "ethers";
import { minifyDecimals } from "based-shared/utils/minify-decimals";

export function Account({ className = "" }: { className?: string }) {
  const { chains, setRouter } = useSimpleKitContext();
  const { chainId, address, network, social, image, users } = useAccount();

  const { chainLogo, nativeSymbol, addressExplorer } = useChainInfo();
  const { data: balance, isPending } = useBalance();

  const canSwitchChain =
    chains
      .filter((item: any) => item.id !== chainId)
      .filter((item: any) => social || item.network.symbol === network).length >
    0;

  const canAddAccount = true;

  const buttons: {
    label: string;
    logo: string;
    onClick: () => void;
  }[] = [
    users.length > 1 && {
      label: "Switch Account",
      logo: AccountLogo,
      onClick: () => {
        setRouter("switchAccount");
      },
    },
    canSwitchChain && {
      label: "Switch Chain",
      logo: chainLogo || WrongChainLogo,
      onClick: () => {
        setRouter(routes.switchChain);
      },
    },
    canAddAccount && {
      label: "Add Account",
      logo: AccountLogo,
      onClick: () => {
        setRouter(routes.home);
      },
    },
    social && {
      label: "Withdraw",
      logo: WithdrawLogo,
      onClick: () => {
        setRouter(routes.withdrawFunds);
      },
    },
  ].filter(Boolean) as {
    label: string;
    logo: string;
    onClick: () => void;
  }[];

  return (
    <div className={cn("web3kit-space-y-4 web3kit-text-center", className)}>
      <motion.img
        initial={{ scale: 0.5, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          duration: 0.5,
          ease: [0.4, 0, 0.2, 1],
          delay: 0.1,
        }}
        src={typeof image === "string" ? image : undefined}
        alt={`avatar for ${address}`}
        className="web3kit-size-24 web3kit-rounded-full web3kit-mx-auto"
      />
      <div className="web3kit-flex web3kit-items-end web3kit-gap-2 web3kit-justify-center">
        <div className="web3kit-text-sm web3kit-leading-none">
          {isPending ? (
            <Skeleton className="web3kit-h-4 web3kit-w-[50px]" />
          ) : (
            `${minifyDecimals(formatUnits(balance || 0n, 18))} ${nativeSymbol}`
          )}
        </div>
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={addressExplorer.replace("{{addr}}", address)}
          className="web3kit-leading-none web3kit-text-primary"
        >
          {minifyAddress(address)}
        </a>
        <CopyAddressButton address={address} />
        <QrCodeIcon
          className="web3kit-size-4 web3kit-cursor-pointer hover:web3kit-text-foreground"
          onClick={() => {
            setRouter(routes.walletqr);
          }}
        />
      </div>
      <div className="web3kit-space-y-2">
        {buttons.map((item, index) => (
          <OptionButton
            key={index}
            index={index}
            className="web3kit-w-full"
            logo={item.logo}
            onClick={item.onClick}
          >
            <div className="web3kit-text-lg">{item.label}</div>
          </OptionButton>
        ))}
      </div>
    </div>
  );
}
